import { memo, useMemo } from 'react';
import cx from 'classnames';
import { groupBy } from 'lodash';
import PropTypes from 'prop-types';

import { CustomTypography } from 'src/components/CustomTypography';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';

import { FILTER_TYPE, MESSAGING_BANK_ASSET, PROMPT_MENU_TYPE } from '../../lib';
import { EmptyMenuState } from '../EmptyMenuState';
import { ItemsBank } from '../ItemsBank';
import { ItemsImage } from '../ItemsImage';
import { ItemsResource } from '../ItemsResource';
import { ItemsUrl } from '../ItemsUrl';

import { useStyles } from './useStyles';

const EMPTY_ARRAY = [];

const propTypes = {
  className: PropTypes.string,
  searchValue: PropTypes.string.isRequired,
  filters: PropTypes.shape().isRequired,
  filteredResources: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  bankItems: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onAction: PropTypes.func.isRequired,
  isApplicationModeInfinity: PropTypes.bool,
  quickSearchMode: PropTypes.bool,
  noTags: PropTypes.bool,
};

const defaultProps = {
  className: '',
  isApplicationModeInfinity: false,
  quickSearchMode: false,
  noTags: false,
};

export const SearchResults = memo(
  ({
    className,
    searchValue,
    filters,
    filteredResources,
    bankItems,
    onAction,
    isApplicationModeInfinity,
    quickSearchMode,
    noTags,
  }) => {
    const styles = useStyles();

    const groupedResources = useMemo(
      () => groupBy(filteredResources, 'contentType'),
      [filteredResources]
    );
    const urlResources = groupedResources[RESOURCE_CONTENT_TYPE.url] || EMPTY_ARRAY;
    const documentResources = groupedResources[RESOURCE_CONTENT_TYPE.document] || EMPTY_ARRAY;
    const imageResources = groupedResources[RESOURCE_CONTENT_TYPE.image] || EMPTY_ARRAY;
    const audioResources = groupedResources[RESOURCE_CONTENT_TYPE.audio] || EMPTY_ARRAY;
    const videoResources = groupedResources[RESOURCE_CONTENT_TYPE.video] || EMPTY_ARRAY;

    const displayUrls = urlResources?.length > 0;
    const displayDocuments = documentResources?.length > 0;
    const displayImages = imageResources?.length > 0;
    const displayAudios = audioResources?.length > 0;
    const displayVideos = videoResources?.length > 0;
    const displayBankMessages =
      bankItems?.length > 0 &&
      !bankItems[0]?.emptyState &&
      (!filters[FILTER_TYPE.types]?.length ||
        filters[FILTER_TYPE.types].includes(MESSAGING_BANK_ASSET)) &&
      filters[FILTER_TYPE.tags].length === 0;

    return (
      <div className={cx(styles.root, className)}>
        {displayUrls && (
          <>
            <CustomTypography className={styles.menuHeader}>Webpages</CustomTypography>

            <ItemsUrl
              urlResources={urlResources}
              onAction={onAction}
              quickSearchMode={quickSearchMode}
              noTags={noTags}
            />
          </>
        )}

        {displayDocuments && (
          <>
            <CustomTypography className={styles.menuHeader}>Files</CustomTypography>

            <ItemsResource
              resources={documentResources}
              onAction={onAction}
              quickSearchMode={quickSearchMode}
              menuType={PROMPT_MENU_TYPE.document}
              noTags={noTags}
            />
          </>
        )}

        {displayBankMessages && (
          <>
            <CustomTypography className={styles.menuHeader}>Messaging bank assets</CustomTypography>

            <ItemsBank
              bankResources={bankItems}
              onAction={onAction}
              isApplicationModeInfinity={isApplicationModeInfinity}
              quickSearchMode={quickSearchMode}
              noTags={noTags}
            />
          </>
        )}

        {displayImages && (
          <>
            <CustomTypography className={styles.menuHeader}>Images</CustomTypography>

            <ItemsImage
              imageResources={imageResources}
              onAction={onAction}
              quickSearchMode={quickSearchMode}
              noTags={noTags}
            />
          </>
        )}

        {displayAudios && (
          <>
            <CustomTypography className={styles.menuHeader}>Audio files</CustomTypography>

            <ItemsResource
              resources={audioResources}
              onAction={onAction}
              quickSearchMode={quickSearchMode}
              menuType={PROMPT_MENU_TYPE.audio}
              noTags={noTags}
            />
          </>
        )}

        {displayVideos && (
          <>
            <CustomTypography className={styles.menuHeader}>Videos</CustomTypography>

            <ItemsResource
              resources={videoResources}
              onAction={onAction}
              quickSearchMode={quickSearchMode}
              menuType={PROMPT_MENU_TYPE.video}
              noTags={noTags}
            />
          </>
        )}

        {!displayUrls &&
          !displayDocuments &&
          !displayImages &&
          !displayAudios &&
          !displayVideos &&
          !displayBankMessages && <EmptyMenuState searchText={searchValue} />}
      </div>
    );
  }
);

SearchResults.displayName = 'SearchResults';
SearchResults.propTypes = propTypes;
SearchResults.defaultProps = defaultProps;
